import { Link } from "react-router";
import type { RecordView } from "@shared-core/domain/object-ui/contract";
import { sharedCommunicationOf } from "@shared-core/domain/shared-communication";
import { paths } from "@shared-core/routing/routes";
import { Card, CardContent } from "../../primitives/card";

/**
 * A shared communication's own words (Ben, K6): the notes or transcript, the
 * customers and contact people it concerns, and the link to the original.
 * One-only: a custom section on the communication's record. Renders nothing
 * on any other record.
 */
export function SharedCommunicationSection({ record }: { record: RecordView }) {
  const communication = sharedCommunicationOf(record);
  if (!communication) return null;
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <h2 className="text-base font-semibold">{communication.kindLabel}</h2>
        <p className="whitespace-pre-wrap text-sm">{communication.text}</p>
        {communication.sourceUrl && (
          <a
            href={communication.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline"
          >
            Open the original
          </a>
        )}
        <dl className="space-y-2 text-sm">
          <div>
            <dt className="text-muted-foreground">Customers</dt>
            <dd>
              {communication.customers.map((customer, n) => (
                <span key={customer.customer_code}>
                  {n > 0 && ", "}
                  <Link
                    to={paths.objectRecord({
                      objectId: "customers.customer",
                      recordKey: customer.customer_code,
                    })}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {customer.name}
                  </Link>
                </span>
              ))}
            </dd>
          </div>
          {communication.people.length > 0 && (
            <div>
              <dt className="text-muted-foreground">Contact people</dt>
              <dd>
                {communication.people.map((person) => person.name).join(", ")}
              </dd>
            </div>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}
